import type {
	AgentExampleAction,
	AgentRecommendationEvidence,
	AgentRecommendationPayload,
} from "@productlane/lib/src/agent-recommendations"
import { examplesNeedingAnswers } from "@productlane/lib/src/agent-recommendations"

import { outcomeLabel } from "./format"

export const IDEA_EXAMPLES_COPY = {
	position: (index: number, total: number) =>
		`Real conversation ${index + 1} of ${total}`,
	// Ideas from before the answers were stored only know the outcome.
	noAnswer: (outcome: AgentRecommendationEvidence["outcome"]) =>
		outcomeLabel(outcome),
	proposes: "What the agent would ask your team to approve",
	previous: "Previous example",
	next: "Next example",
	openChat: "Open chat",
	change: "The change",
	customer: "Customer",
}

/** Wraps around, so the arrows never dead-end. */
export function stepExample(index: number, delta: number, total: number) {
	if (total <= 0) return 0
	return (((index + delta) % total) + total) % total
}

/** The preview shape the Stripe approval card reads its before and after rows from. */
export function exampleActionPreview(
	action: AgentExampleAction,
): Record<string, unknown> {
	switch (action.actionType) {
		case "change_plan":
			return {
				before: { price: { nickname: action.before } },
				after: { price: { nickname: action.after } },
			}
		case "extend_trial":
			return {
				before: { trialEnd: action.before },
				after: { trialEnd: action.after },
			}
		case "cancel_subscription":
			return { after: { periodEnd: action.after } }
		case "apply_promotion_code":
		case "remove_promotion_code":
			return {
				before: { discount: { description: action.before } },
				after: { discount: { description: action.after } },
			}
	}
}

// The server writes the answers within a minute; past that the column falls
// back to what is stored instead of loading forever.
export const EXAMPLE_BACKFILL_TIMEOUT_MS = 90_000

/** Asks the server once per idea per session, and only for an idea missing answers. */
export function shouldRequestExampleAnswers(
	recommendationId: string,
	payload: AgentRecommendationPayload | null | undefined,
	requested: ReadonlySet<string>,
): boolean {
	return (
		!requested.has(recommendationId) &&
		examplesNeedingAnswers(payload).length > 0
	)
}

/** Whether one example is still waiting on the answers the server is writing. */
export function isExampleLoading({
	payload,
	threadId,
	startedAt,
	now,
}: {
	payload: AgentRecommendationPayload | null | undefined
	threadId: string
	startedAt: number | null
	now: number
}): boolean {
	if (startedAt === null || now - startedAt > EXAMPLE_BACKFILL_TIMEOUT_MS) {
		return false
	}
	return examplesNeedingAnswers(payload).some((e) => e.threadId === threadId)
}
